// Vendors Intelligence Workspace — Slice 2 BI layer (fuller KPIs + real trends + findings + the
// prominent attention list) above the vendors table. Feeds from the population GET /vendors/bi-summary.
// ⚠ ACTIVE-ONLY (retired excluded) + scope on-face (Design #2). ⚠ Trends ONLY where real history exists;
// findings-burndown renders the honest "not enough history" card (Design #4/#6 — never reconstructed).
// ⚠ NO composite / avg-composite / external-rating (Design #6). `—` not `0`. Trap #55 tokens (recharts
// takes the SAME token hexes since it needs color strings, not classes). Filters/URL/table = Slice 3.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, ComposedChart, AreaChart, Area, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { usePortfolioBiSummary, type PortfolioBiRange, type PortfolioAttentionRow } from '@/api/hooks/useVendors';
import { Badge } from '@/components/ui/badge';
import { InfoTooltip } from '@/components/shared/InfoTooltip';

// Token hexes (Trap #55 — recharts needs color strings; these mirror tailwind.config.ts exactly).
const C = {
  critical: '#C2453F', high: '#E07B4F', medium: '#D7A94A', low: '#4FA8E8', info: '#6E86A2',
  valid: '#3FB980', warn: '#D7A94A', expired: '#C2453F', cyan: '#009FFD', ai: '#8B6DF0',
  axis: '#5A6A82', grid: '#1E2E42', muted: '#8DA0B8', panel: '#111E30', border: '#1E2E42',
};

// Tier/lifecycle/severity/outcome DistRow metas all removed — those distribution tiles moved off /vendors.
const TIER_SHORT: Record<string, string> = { tier_1_critical: 'Tier 1', tier_2_high: 'Tier 2', tier_3_medium: 'Tier 3', tier_4_low: 'Tier 4' };
const RANGES: PortfolioBiRange[] = ['30d', '90d', '6m', '12m'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function bucketLabel(iso: string, gran: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return gran === 'month' ? MONTHS[d.getUTCMonth()] : `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function Kpi({ label, value, accent, note, onClick, info }: { label: string; value: string; accent?: ReactNode; note?: ReactNode; onClick?: () => void; info?: string }) {
  return (
    <div
      onClick={onClick}
      className={`bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-1.5 ${onClick ? 'cursor-pointer hover:border-accent-cyan/50 transition-colors' : ''}`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary flex items-center gap-1">{label}{info && <InfoTooltip text={info} />}</span>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-bold text-text-primary">{value}</span>
        {accent}
      </div>
      {note && <span className="text-[11px] text-text-secondary">{note}</span>}
    </div>
  );
}

function Panel({ title, subtitle, children, info, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; info?: string; footer?: ReactNode }) {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-text-primary flex items-center gap-1">{title}{info && <InfoTooltip text={info} />}</span>
        {subtitle && <span className="text-[11px] text-text-secondary">{subtitle}</span>}
      </div>
      {children}
      {footer && <div className="flex items-center gap-2 flex-wrap pt-1">{footer}</div>}
    </div>
  );
}

// Legend swatch for the multi-series line charts (findings-by-severity).
function LegendDot({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10.5px] text-text-secondary">
      <span className="inline-block w-3 h-0.5 rounded-full" style={{ background: dashed ? 'transparent' : color, borderTop: dashed ? `1.5px dashed ${color}` : undefined }} />
      {label}
    </span>
  );
}

const axis = { tick: { fill: C.axis, fontSize: 10 }, axisLine: false, tickLine: false };
const tooltipStyle = { contentStyle: { background: '#0E1A2B', border: `1px solid ${C.grid}`, borderRadius: 8, fontSize: 12 } };

function TrendGap({ title, note, info }: { title: string; note: string; info?: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border-subtle bg-bg-base/50 p-4 flex flex-col gap-2">
      <span className="text-sm font-semibold text-text-secondary inline-flex items-center gap-1.5">{title}{info && <InfoTooltip text={info} />}</span>
      <span className="text-[11px] uppercase tracking-wide text-text-faint">Not enough history</span>
      <span className="text-[11.5px] text-text-faint leading-relaxed">{note}</span>
    </div>
  );
}

export function VendorsPortfolioBi({ onSegment }: { onSegment?: (key: string, value: string | null) => void }) {
  const [range, setRange] = useState<PortfolioBiRange>('90d');
  const { data: bi, isError } = usePortfolioBiSummary(range);
  if (isError) return null; // defensive — never block the table on a BI fetch error

  const k = bi?.kpis;
  const scope = bi?.scope;
  const t = bi?.trends;
  const fmt = (n?: number) => (n == null ? '—' : String(n));
  // Drill-through: KPI/chart/bar click → sets the URL filter (drives table + viz). ⚠ Distinct from the
  // attention-row NAVIGATE gesture. Trend time-buckets are temporal → NOT filter targets (informational).
  const seg = (key: string, value: string) => (onSegment ? () => onSegment(key, value) : undefined);


  // Trend rows for recharts
  const gran = t?.granularity ?? 'week';
  const trendRows = t ? t.buckets.map((b, i) => ({
    x: bucketLabel(b, gran),
    total: t.population.total[i], added: t.population.added[i], retired: t.population.retired[i],
    pass: t.posture.pass[i], conditional: t.posture.conditional[i], fail: t.posture.fail[i],
    issued: t.activity.issued[i], submitted: t.activity.submitted[i], completed: t.activity.completed[i],
  })) : [];
  const sum = (a?: number[]) => (a ? a.reduce((x, y) => x + y, 0) : 0);
  const postureAny = t ? sum(t.posture.pass) + sum(t.posture.conditional) + sum(t.posture.fail) > 0 : false;
  const activityAny = t ? sum(t.activity.issued) + sum(t.activity.submitted) + sum(t.activity.completed) > 0 : false;
  // Posture pass rate as an overall FOOTER figure (Design: rate is a footer chip, NOT a second axis). Across
  // the range = Σpass ÷ Σcompleted. ⚠ null (shown as "—") when nothing completed — never a fabricated 0%.
  const postureCompleted = t ? sum(t.posture.pass) + sum(t.posture.conditional) + sum(t.posture.fail) : 0;
  const posturePassRate = postureCompleted > 0 ? Math.round((sum(t?.posture.pass) / postureCompleted) * 100) : null;
  // Findings raised over time by severity (Design #3: one line per severity + a dashed Unspecified line).
  // ⚠ These are RAISED counts, so a 0-bucket is a TRUE measured zero (dot at every point, connect adjacent) —
  // distinct from posture's pass-rate, where 0-completed is undefined and must be a gap, never 0%.
  const fs = t?.findings_severity;
  const fsArr = (key: string) => (fs as Record<string, number[]> | undefined)?.[key];
  const findingsRows = t ? t.buckets.map((b, i) => ({
    x: bucketLabel(b, gran),
    critical: fs?.critical[i] ?? 0, high: fs?.high[i] ?? 0, medium: fs?.medium[i] ?? 0,
    low: fs?.low[i] ?? 0, informational: fs?.informational[i] ?? 0, unspecified: fs?.unspecified[i] ?? 0,
  })) : [];
  const findingsAny = fs ? (sum(fs.critical) + sum(fs.high) + sum(fs.medium) + sum(fs.low) + sum(fs.informational) + sum(fs.unspecified)) > 0 : false;
  const FINDING_LINES: [string, string, string, boolean][] = [
    // key, label, color, dashed
    ['critical', 'Critical', C.critical, false], ['high', 'High', C.high, false], ['medium', 'Medium', C.medium, false],
    ['low', 'Low', C.low, false], ['informational', 'Info', C.info, false], ['unspecified', 'Unspecified', C.muted, true],
  ];
  const addedTotal = sum(t?.population.added), retiredTotal = sum(t?.population.retired);
  const totalNow = t && t.population.total.length ? t.population.total[t.population.total.length - 1] : (scope?.total_on_file ?? 0);

  return (
    <div className="flex flex-col gap-4">
      {/* Range selector (local; URL persistence = Slice 3). Point-in-time KPIs stay current — only deltas/trends follow it. */}
      <div className="flex items-center gap-1.5">
        {RANGES.map((r) => (
          <button key={r} type="button" onClick={() => setRange(r)}
            className={`text-xs font-semibold rounded-md px-2.5 py-1 border transition-colors ${range === r ? 'bg-accent-cyan/15 border-accent-cyan/50 text-accent-cyan' : 'border-border-subtle text-text-secondary hover:text-text-primary'}`}>
            {r}
          </button>
        ))}
      </div>

      {/* KPI row (6) — active-only; scope on-face (Design #2) */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
        <Kpi label="Active vendors" value={fmt(k?.active_vendors)}
          accent={bi ? <span className="text-xs font-bold text-cert-valid-fg">+{bi.deltas.vendors_added} added</span> : undefined}
          note={scope ? `excludes ${scope.retired_vendors} retired · active only` : 'active only'}
          info="Vendors not offboarded (retired excluded). The +N is vendors added within the selected range." />
        <Kpi label="Critical & high tier" value={fmt(k?.critical_high_tier)} note={k ? `of ${k.active_vendors} active` : undefined}
          onClick={seg('tiers', 'tier_1_critical,tier_2_high')}
          info="Active vendors in Tier 1 (Critical) or Tier 2 (High)." />
        <Kpi label="Assessments overdue" value={fmt(k?.assessments_overdue)}
          accent={k && k.assessments_overdue > 0 ? <span className="text-xs font-bold text-cert-expired-fg">overdue</span> : undefined}
          note={k ? `of ${k.assessments_in_flight} in flight` : undefined} onClick={seg('overdue', 'true')}
          info="Assessments past their due date and not yet approved, rejected, or cancelled — active vendors only." />
        <Kpi label="Open findings" value={fmt(k?.open_findings)}
          note={k ? (<>{k.open_findings_critical > 0 ? <span className="text-severity-critical font-semibold">{k.open_findings_critical} critical</span> : '0 critical'}{` across ${k.open_findings_vendors} vendors`}</>) : undefined}
          onClick={seg('has_findings', 'true')}
          info="Flagged assessment responses on active vendors' non-terminal assessments (cancelled/expired excluded)." />
        <Kpi label="Certs expiring ≤90d" value={fmt(k?.certs_expiring_90d)}
          accent={k && k.certs_expired > 0 ? <span className="text-xs font-bold text-cert-expired-fg">{k.certs_expired} expired</span> : undefined}
          note={k ? 'active vendors' : undefined} onClick={seg('cert_expiring', 'true')}
          info="Active-vendor certifications expiring within the next 90 days (already-expired counted separately)." />
        <Kpi label="Risk responses" value={fmt(k?.risk_acceptances_open)}
          note={k ? `open · ${k.risk_acceptances_expiring_60d} expire ≤60d` : undefined} onClick={seg('ra_open', 'true')}
          info="Open (approved or pending-approval) risk responses on active vendors; the expiring count is those due within 60 days." />
      </div>

      {/* Trends: population + security posture (Design #3, real history) */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-3">
        <Panel title="Vendor population" info="Total vendors on file over time (created minus deleted). Added and retired are the per-period flows; the total series includes retired vendors." subtitle={t ? `+${addedTotal} added · −${retiredTotal} retired · ${totalNow} on file` : undefined}>
          {trendRows.length === 0 ? <p className="text-xs text-text-secondary">No history in this range.</p> : (
            <ResponsiveContainer width="100%" height={170}>
              <AreaChart data={trendRows} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                <defs><linearGradient id="vpTotal" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.cyan} stopOpacity={0.28} /><stop offset="100%" stopColor={C.cyan} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="x" {...axis} interval="preserveStartEnd" />
                <YAxis {...axis} allowDecimals={false} width={34} />
                <Tooltip {...tooltipStyle} />
                <Area type="monotone" dataKey="total" name="on file" stroke={C.cyan} strokeWidth={2} fill="url(#vpTotal)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Security posture over time"
          info="Completed assessments per period by outcome (bars). A period with no completions renders no bar at all — never a zero-height bar or 0%, which would read as 'everything failed'. The pass rate below is pass ÷ all completed across the range. Active vendors only."
          subtitle="completed assessments · pass / conditional / fail"
          footer={postureAny ? (
            <span className="text-[11px] font-semibold rounded-md border border-border-subtle bg-bg-surface-2 px-2 py-0.5 text-text-secondary">
              Pass rate <span className="text-cert-valid-fg font-bold tabular-nums">{posturePassRate == null ? '—' : `${posturePassRate}%`}</span>
              <span className="text-text-faint font-normal"> · {sum(t?.posture.pass)}/{postureCompleted} completed</span>
            </span>
          ) : undefined}>
          {!postureAny ? <TrendGapInline note="No assessments were completed in this range — the posture trend appears once scored assessments land in the window." /> : (
            <ResponsiveContainer width="100%" height={170}>
              {/* Stacked bars only (Design): pass-rate is the footer chip, not a second axis. Empty weeks = no bar. */}
              <ComposedChart data={trendRows} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="x" {...axis} interval="preserveStartEnd" />
                <YAxis {...axis} allowDecimals={false} width={34} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="pass" stackId="p" name="pass" fill={C.valid} maxBarSize={16} />
                <Bar dataKey="conditional" stackId="p" name="conditional" fill={C.warn} maxBarSize={16} />
                <Bar dataKey="fail" stackId="p" name="fail" fill={C.expired} radius={[2, 2, 0, 0]} maxBarSize={16} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Assessment activity + findings-by-severity time-series + the honest burn-down gap */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-3">
        <Panel title="Assessment activity" info="Assessments issued, submitted, and completed per period — active vendors." subtitle="issued / submitted / completed">
          {!activityAny ? <TrendGapInline note="No assessment activity in this range yet." /> : (
            <ResponsiveContainer width="100%" height={170}>
              <ComposedChart data={trendRows} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="x" {...axis} interval="preserveStartEnd" />
                <YAxis {...axis} allowDecimals={false} width={34} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="issued" name="issued" fill={C.low} radius={[2, 2, 0, 0]} />
                <Bar dataKey="submitted" name="submitted" fill={C.ai} radius={[2, 2, 0, 0]} />
                <Bar dataKey="completed" name="completed" fill={C.valid} radius={[2, 2, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Open findings by severity"
          info="Findings raised per period, one line per severity. Findings whose severity was never set (optional at flag time) appear as the dashed Unspecified line — counted, not hidden, and not assumed low. Active vendors only."
          subtitle={k ? `${k.open_findings} open · ${k.open_findings_vendors} vendors` : undefined}
          footer={findingsAny ? FINDING_LINES.filter(([key]) => sum(fsArr(key)) > 0).map(([key, label, color, dashed]) => <LegendDot key={key} color={color} label={label} dashed={dashed} />) : undefined}>
          {!findingsAny ? <TrendGapInline note="No findings were raised in this range — the severity lines appear once flagged responses land in the window." /> : (
            <ResponsiveContainer width="100%" height={170}>
              {/* One line per severity; Unspecified is slate + dashed. Dots at every point; adjacent points connect. */}
              <ComposedChart data={findingsRows} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="x" {...axis} interval="preserveStartEnd" />
                <YAxis {...axis} allowDecimals={false} width={34} />
                <Tooltip {...tooltipStyle} />
                {FINDING_LINES.filter(([key]) => sum(fsArr(key)) > 0).map(([key, label, color, dashed]) => (
                  <Line key={key} type="monotone" dataKey={key} name={label} stroke={color} strokeWidth={dashed ? 1.6 : 2}
                    strokeDasharray={dashed ? '5 3' : undefined} dot={{ r: 1.8, fill: C.panel, stroke: color, strokeWidth: 1.3 }} connectNulls />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>
      {t && !t.findings_burndown.available && (
        <TrendGap title="Findings opened vs remediated" note={t.findings_burndown.since_note} info="Findings opened vs. remediated over the range. Shown only once there is enough history in the window; until then this is a gap, not a zero line — active vendors only." />
      )}

      {/* ⚠ Vendors requiring attention — the lead operational section (Design #1: outranks the charts) */}
      <div className="bg-bg-surface border border-severity-high/40 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">Vendors requiring attention<InfoTooltip text="Active vendors with an overdue assessment, an open critical/high finding, a certification expiring soon, or an open risk response. Retired (offboarded) vendors are excluded." /></span>
            {bi && <Badge variant={bi.attention.length > 0 ? 'rose' : 'muted'} className="font-mono">{bi.attention.length}</Badge>}
          </div>
          <span className="text-[11px] text-text-secondary">active vendors only · union of real signals</span>
        </div>
        {!bi ? (
          <p className="text-xs text-text-secondary">—</p>
        ) : bi.attention.length === 0 ? (
          <p className="text-sm text-text-secondary">No vendors currently require attention.</p>
        ) : (
          <div className="flex flex-col divide-y divide-border-subtle">
            {bi.attention.slice(0, 12).map((row) => <AttentionRow key={row.vendor_id} row={row} />)}
          </div>
        )}
      </div>
      {/* ⚠ "Assessment outcomes" distribution MOVED to /assessments (Item 3) — it belongs with the
          assessment workspace, distinct from that page's "Outcomes over time" series. Removed here. */}
    </div>
  );
}

function TrendGapInline({ note }: { note: string }) {
  return (
    <div className="h-[170px] flex flex-col items-center justify-center gap-1.5 text-center px-4 border border-dashed border-border-subtle rounded-lg">
      <span className="text-[11px] uppercase tracking-wide text-text-faint">Not enough history</span>
      <span className="text-[11.5px] text-text-faint leading-relaxed max-w-[36ch]">{note}</span>
    </div>
  );
}

const SIGNAL_CLS: Record<string, string> = {
  overdue: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border',
  cert: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border',
  ra: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border',
  findings: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border',
};

function AttentionRow({ row }: { row: PortfolioAttentionRow }) {
  return (
    <Link to={`/vendors/${row.vendor_id}`} className="flex items-start justify-between gap-3 py-2.5 hover:bg-bg-surface-2/60 -mx-4 px-4 transition-colors">
      <div className="min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary truncate">{row.vendor_name}</span>
          <Badge variant="muted" className="text-[10px] py-0 shrink-0">{TIER_SHORT[row.tier] ?? row.tier}</Badge>
        </div>
        <span className="text-[11.5px] text-text-secondary">{row.signals.map((s) => s.reason).join(' · ')}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end max-w-[45%]">
        {row.signals.map((s, i) => (
          <span key={i} className={`text-[10px] font-bold rounded-md border px-1.5 py-0.5 ${SIGNAL_CLS[s.type] ?? 'bg-bg-surface-2 text-text-muted border-border-subtle'}`}>{s.label}</span>
        ))}
      </div>
    </Link>
  );
}
